import { type Accessor, createContext, createEffect, createMemo, onCleanup, type ParentProps, useContext } from "solid-js"
import { tryUseServer } from "@/context/server"
import { useNavigate } from "@/utils/navigation"

export function createMachineResumeState() {
  const resumed = new Set<string>()
  return {
    route(machine: string, loadRoute: (machine: string) => string | undefined) {
      if (!machine || resumed.has(machine)) return undefined
      return loadRoute(machine)
    },
    markResumed(machine: string) {
      resumed.add(machine)
    },
    dispose() {
      resumed.clear()
    },
  }
}

type MachineResumeState = ReturnType<typeof createMachineResumeState>
const MachineResumeContext = createContext<MachineResumeState>()

/**
 * The chat a machine's home restores: the one the user left, once per load.
 * Home shown with nothing left to restore is where the user is, so the
 * machine's remembered chat is forgotten and the next launch (an installed web
 * app relaunches at its start URL) opens home too.
 */
export function createMachineHomeResume(input: {
  machine: Accessor<string>
  state: MachineResumeState
  routes: {
    lastSessionRoute: (machine: string) => string | undefined
    forgetSessionRoute: (machine: string) => void
  }
}) {
  const route = createMemo(() => input.state.route(input.machine(), input.routes.lastSessionRoute))
  createEffect(() => {
    const machine = input.machine()
    if (machine && !route()) input.routes.forgetSessionRoute(machine)
  })
  return route
}

export function MachineResumeController(props: ParentProps) {
  const state = createMachineResumeState()
  onCleanup(state.dispose)
  return <MachineResumeContext.Provider value={state}>{props.children}</MachineResumeContext.Provider>
}

export function tryUseMachineResume() {
  return useContext(MachineResumeContext)
}

export function useMachineResume() {
  const state = tryUseMachineResume()
  if (!state) throw new Error("useMachineResume must be used inside MachineResumeController")
  return state
}

/**
 * Opens the machine's dashboard home itself. Home otherwise restores the
 * machine's last chat once per load, which is the chat the user just left.
 */
export function useOpenMachineHome() {
  const resume = tryUseMachineResume()
  const server = tryUseServer()
  const navigate = useNavigate()
  return () => {
    if (server?.name) resume?.markResumed(server.name)
    navigate("/", { replace: true })
  }
}
